import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { AlertTriangle, CheckCircle, CreditCard, DownloadCloud, Edit2, Key, Plus, Power, RefreshCw } from "react-feather";
import {
  Button, Container, Modal, ModalBody, ModalFooter, ModalHeader,
} from "reactstrap";
import {
  createSpanMachine,
  fetchBranches,
  generateSpanMachineToken,
  getMerchantListing,
  getSpanMachineList,
  syncSpanMachines,
  updateSpanMachine,
} from "../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Tile, Loader as RLoader } from "../reporting/ReportingShared";
import NearpayAccountCard from "./nearpay-account-card";
import KioskMerchantSyncCard from "./kiosk-merchant-sync-card";

/**
 * The merchant's payment-terminal registry.
 *
 * A terminal is infrastructure, not a screen setting: it has its own identity, credential,
 * environment and on/off state, and one day POS and self-service devices will read from the same
 * list. So it lives here, at merchant level, and the signage screen page only picks from it.
 *
 * TWO RULES THIS PAGE EXISTS TO MAKE VISIBLE
 *
 *  - A terminal serves ONE screen. The Assigned column shows which, so an admin sees a terminal is
 *    taken before trying to assign it rather than after the API refuses.
 *  - A terminal is only usable by devices in ITS OWN branch. Branch is therefore required at
 *    creation and shown on every row.
 *
 * There is no delete. A terminal is named by every tender line it ever produced, so removing one
 * would leave payments whose origin cannot be resolved. Retiring it is the Active toggle.
 *
 * Switching one off takes the screen holding it OUT OF SERVICE — it keeps showing the menu but stops
 * accepting orders. It does NOT fall back to orders settled at the counter: a screen that was taking
 * card is a card-only screen, and quietly turning it into one that hands out unpaid orders is not a
 * decision this toggle should be able to make. Screens with no terminal at all are unaffected.
 *
 * The credential is write-only. The API returns a masked hint and never the JWT, because the
 * browser has no use for the real value — only the device does, and it fetches its own.
 */

const EMPTY_FORM = {
  name: "",
  spanType: "nearpay",
  terminalId: "",
  environment: "production",
  credential: "",
  merchant_span_machine_id: "",
  notes: "",
  MerchantId: "",
  branchId: "",
  active: true,
};

const PAGE_TABS = [
  { key: "terminals", label: "Terminals" },
  { key: "account", label: "NearPay Account" },
  { key: "kiosk", label: "Kiosk Orders → POS" },
];

// Same tab strip as the Recommendations page.
const Tabs = ({ tabs, value, onChange }) => (
  <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
    {tabs.map((t) => (
      <button
        key={t.key}
        onClick={() => onChange(t.key)}
        style={{
          padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
          border: t.key === value ? `1.5px solid ${C.coral}` : `1px solid ${C.border}`,
          background: t.key === value ? C.coralLight : C.white, color: t.key === value ? C.coral : C.text,
        }}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const TerminalList = () => {
  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  // Which merchant a pull acts on: the app header's selector, the same source the NearPay Account
  // card reads. Both read it directly rather than one passing it to the other — a single source with
  // two readers cannot drift, and there is nothing for a stale prop to be stale about.
  const { selectedMerchant } = useSelector((state) => state.merchant || {});
  const syncMerchantId = isAdmin ? selectedMerchant || "" : "";

  const [tab, setTab] = useState("terminals");
  const [terminals, setTerminals] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const [merchants, setMerchants] = useState([]);

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [branches, setBranches] = useState([]);

  // Terminal awaiting a "yes, replace the live credential" confirmation, and the row currently
  // being signed (so its spinner is per-row rather than blocking the whole table).
  const [tokenTarget, setTokenTarget] = useState(null);
  const [tokenBusyId, setTokenBusyId] = useState(null);

  const fetchTerminals = useCallback(async () => {
    setIsFetching(true);
    const data = await getSpanMachineList();
    setTerminals(data || []);
    setIsFetching(false);
  }, []);

  useEffect(() => {
    fetchTerminals();
  }, [fetchTerminals]);

  const loadBranches = useCallback(async (merchantId) => {
    // A merchant user's own branches come back without a filter; an admin has to say whose.
    const params = merchantId ? { MerchantId: merchantId } : {};
    const result = await fetchBranches(params);
    setBranches(result?.data || []);
  }, []);

  const openCreate = async () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setBranches([]);
    if (isAdmin) {
      const merchantData = await getMerchantListing();
      setMerchants(merchantData || []);
    } else {
      await loadBranches(null);
    }
    setModalOpen(true);
  };

  const openEdit = async (terminal) => {
    setEditing(terminal);
    setForm({
      name: terminal.name || "",
      spanType: terminal.spanType || "nearpay",
      terminalId: terminal.terminalId || "",
      environment: terminal.environment || "production",
      // Blank on open: an empty credential means "leave the stored one alone", so an admin
      // renaming a terminal cannot wipe its JWT by saving the form.
      credential: "",
      merchant_span_machine_id: terminal.merchant_span_machine_id || "",
      notes: terminal.notes || "",
      MerchantId: terminal.merchant?.id || "",
      branchId: terminal.branch?.id || "",
      active: !!terminal.active,
    });
    await loadBranches(terminal.merchant?.id || null);
    setModalOpen(true);
  };

  const handleMerchantChange = async (merchantId) => {
    // Clear the branch with the merchant: keeping a stale branch id would post a branch belonging
    // to somebody else, which the API rejects and the admin would have to diagnose.
    setForm((f) => ({ ...f, MerchantId: merchantId, branchId: "" }));
    setBranches([]);
    if (merchantId) await loadBranches(merchantId);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    if (!form.branchId) return;

    setIsSaving(true);

    const payload = {
      name: form.name.trim(),
      spanType: form.spanType,
      terminalId: form.terminalId?.trim() || null,
      environment: form.environment,
      // merchant_span_machine_id is deliberately NOT sent. It is the merchant's own id for this span
      // machine and the sync owns it; posting the form's copy back would let a stale render overwrite
      // a value the sync had just written.
      notes: form.notes?.trim() || null,
      branchId: form.branchId,
      active: form.active,
      // Only sent when the admin actually typed one — see the comment in openEdit.
      ...(form.credential?.trim() && { credential: form.credential.trim() }),
      ...(isAdmin && form.MerchantId && { MerchantId: form.MerchantId }),
    };

    const result = editing
      ? await updateSpanMachine(editing.id, payload)
      : await createSpanMachine(payload);

    setIsSaving(false);

    if (result) {
      setModalOpen(false);
      fetchTerminals();
    }
  };

  // Pull the registry from the merchant's own POS system.
  //
  // The report is shown even when everything imported cleanly. A sync that quietly says "done" trains
  // an operator not to read it, and the skipped rows are the half that needs a human: a terminal
  // linked to several branches, or one a POS tablet already owns, can only be fixed on the merchant
  // side.
  const handleSync = async () => {
    if (isAdmin && !syncMerchantId) return;

    setIsSyncing(true);
    const result = await syncSpanMachines(isAdmin ? syncMerchantId : null);
    setIsSyncing(false);

    if (result.ok) {
      setSyncReport(result.report);
      fetchTerminals();
    }
    // On failure apiCalls has already surfaced the reason; nothing useful to render here.
  };

  const toggleActive = async (terminal) => {
    const result = await updateSpanMachine(terminal.id, { active: !terminal.active });
    if (result) fetchTerminals();
  };

  // Signing a token is the one action here that changes what a live screen is holding, so it is the
  // one that asks first. Generating the FIRST token is harmless; regenerating invalidates the
  // credential the screen is authenticated with right now, and it will be down until its next
  // payment-config poll picks the new one up.
  const requestToken = (terminal) => {
    if (!terminal.hasCredential) {
      generateToken(terminal);
      return;
    }
    setTokenTarget(terminal);
  };

  const generateToken = async (terminal) => {
    setTokenBusyId(terminal.id);
    const result = await generateSpanMachineToken(terminal.id);
    setTokenBusyId(null);
    setTokenTarget(null);
    if (result) fetchTerminals();
  };

  const counts = useMemo(() => {
    const list = terminals || [];
    return {
      total: list.length,
      active: list.filter((t) => t.active).length,
      assigned: list.filter((t) => t.assignedScreen).length,
      // A terminal with no credential cannot take a payment, so it is worth counting rather than
      // leaving an admin to spot the blank hint in a row.
      needsCredential: list.filter((t) => !t.hasCredential).length,
    };
  }, [terminals]);

  const filtered = (terminals || []).filter((t) => {
    if (statusFilter === "active" && !t.active) return false;
    if (statusFilter === "inactive" && t.active) return false;
    if (statusFilter === "unassigned" && t.assignedScreen) return false;
    if (statusFilter === "no-credential" && t.hasCredential) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${t.name || ""} ${t.terminalId || ""} ${t.branch?.name || ""} ${t.merchant?.name || ""} ${t.assignedScreen?.screenCode || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // ---- Shared pieces ----
  const Pill = ({ tone, label }) => {
    const map = {
      green: { bg: C.greenLight, fg: C.green },
      orange: { bg: C.orangeLight, fg: C.orange },
      grey: { bg: C.borderLight, fg: C.textMed },
      teal: { bg: C.coralLight, fg: C.coral },
    };
    const s = map[tone] || map.grey;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "4px 10px",
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          background: s.bg,
          color: s.fg,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.fg }} />
        {label}
      </span>
    );
  };

  const ActionIcon = ({ icon: Icon, color, title, onClick, disabled = false }) => (
    <div
      onClick={disabled ? undefined : onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s",
        border: `1px solid ${C.border}`,
        background: C.white,
        opacity: disabled ? 0.45 : 1,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = C.coralLight;
        e.currentTarget.style.borderColor = C.coral;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = C.white;
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <Icon size={15} color={color} />
    </div>
  );

  const filterSelectStyle = {
    padding: "7px 12px",
    fontSize: 12,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    cursor: "pointer",
    minWidth: 150,
    transition: "border 0.15s",
  };

  const searchInputStyle = { ...filterSelectStyle, cursor: "text", minWidth: 260 };

  const modalLabelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
  };

  const modalInputStyle = {
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    padding: "10px 12px",
    fontSize: 13,
    width: "100%",
    fontFamily: "inherit",
    color: C.text,
    background: C.white,
    outline: "none",
  };

  const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "8px 20px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
  };

  const sectionTitleStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: C.textMed,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  };

  // One line per terminal in the pull report. Wraps rather than truncating: the reason is the whole
  // point of the row, so cutting it off to keep the line tidy would defeat the report.
  const reportRowStyle = {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "baseline",
    fontSize: 13,
    color: C.text,
    padding: "6px 10px",
    borderRadius: 6,
    background: C.bg,
    marginBottom: 4,
    lineHeight: 1.5,
  };

  const canSave = !!form.name?.trim() && !!form.branchId && (!isAdmin || !!form.MerchantId || !!editing);

  return (
    <Fragment>
      <Breadcrumb title="Payment Terminals" parent="Settings" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <Tabs tabs={PAGE_TABS} value={tab} onChange={setTab} />

          {/* The signing key is what makes the Generate Token buttons work; it now has its own tab,
              so the NearPay Account card's status chips are where a missing key shows. */}
          {tab === "account" && (
            <NearpayAccountCard isAdmin={isAdmin} onChanged={fetchTerminals} />
          )}

          {tab === "kiosk" && <KioskMerchantSyncCard isAdmin={isAdmin} />}

          {tab === "terminals" && (
            <>
              {/* KPI Tiles */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 16,
                  marginBottom: 24,
                }}
              >
                <Tile label="Total Terminals" value={counts.total} icon="💳" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                <Tile label="Active" value={counts.active} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
                <Tile label="Assigned" value={counts.assigned} icon="🖥️" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={120} />
                <Tile label="Missing Credential" value={counts.needsCredential} icon="🔑" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={180} />
              </div>

              <RCard>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    onClick={fetchTerminals}
                    title="Refresh"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      border: `1.5px solid ${C.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      background: C.white,
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = C.coral;
                      e.currentTarget.style.background = C.coralLight;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = C.border;
                      e.currentTarget.style.background = C.white;
                    }}
                  >
                    <RefreshCw size={15} color={C.textMed} />
                  </div>

                  <input
                    placeholder="Search name, TID, branch, screen..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={searchInputStyle}
                  />

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={filterSelectStyle}
                  >
                    <option value="all">All Terminals</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="unassigned">Unassigned</option>
                    <option value="no-credential">Missing Credential</option>
                  </select>

                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.text,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <CreditCard size={16} color={C.coral} />
                    {isAdmin ? "All Terminals" : "My Terminals"}
                  </span>

                  {/* No merchant picker of its own: it pulls from whichever merchant the app header
                  names. A page-local selection can disagree with the header, and an operator who
                  uploads a key for one merchant then pulls another's registry has no way to notice. */}
                  <Button
                    onClick={handleSync}
                    disabled={isSyncing || (isAdmin && !syncMerchantId)}
                    style={{
                      ...primaryBtnStyle,
                      background: C.white,
                      color: C.textMed,
                      border: `1.5px solid ${C.border}`,
                      opacity: isSyncing || (isAdmin && !syncMerchantId) ? 0.5 : 1,
                    }}
                    title={isAdmin && !syncMerchantId
                      ? "Pick a merchant in the header first"
                      : "Fetch this merchant's terminals from their POS system"}
                  >
                    <DownloadCloud size={14} /> {isSyncing ? "Pulling…" : "Pull Terminals"}
                  </Button>

                  <Button onClick={openCreate} style={primaryBtnStyle}>
                    <Plus size={14} /> Add Terminal
                  </Button>
                </div>

                {isFetching ? (
                  <RLoader />
                ) : filtered.length === 0 ? (
                  <div style={{ padding: 48, textAlign: "center", color: C.textLight }}>
                    {terminals.length === 0
                      ? "No payment terminals yet. Add one, then assign it to a screen from the screen's settings."
                      : "No terminals match this filter."}
                  </div>
                ) : (
                  <div
                    style={{
                      overflowX: "auto",
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>Terminal</th>
                          <th style={tbl.th}>Type</th>
                          <th style={tbl.th}>TID</th>
                          {isAdmin && <th style={tbl.th}>Merchant</th>}
                          <th style={tbl.th}>Branch</th>
                          <th style={tbl.th}>Environment</th>
                          <th style={tbl.th}>Credential</th>
                          <th style={tbl.th}>Assigned To</th>
                          <th style={tbl.th}>Status</th>
                          <th style={{ ...tbl.th, textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((t) => (
                          <tr key={t.id}>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>
                              {t.name}
                              {!!t.notes && (
                                <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400, marginTop: 2 }}>
                                  {t.notes}
                                </div>
                              )}
                            </td>
                            <td style={tbl.td}>
                              <Pill tone="teal" label={String(t.spanType || "").toUpperCase()} />
                            </td>
                            <td style={{ ...tbl.td, fontVariantNumeric: "tabular-nums" }}>
                              {t.terminalId || <span style={{ color: C.textLight }}>—</span>}
                            </td>
                            {isAdmin && (
                              <td style={tbl.td}>{t.merchant?.name || <span style={{ color: C.textLight }}>—</span>}</td>
                            )}
                            <td style={tbl.td}>{t.branch?.name || <span style={{ color: C.textLight }}>—</span>}</td>
                            <td style={tbl.td}>
                              {/* A production screen running a sandbox credential takes no real money,
                              so the mismatch is worth colouring rather than printing plainly. */}
                              <Pill
                                tone={t.environment === "production" ? "green" : "orange"}
                                label={t.environment}
                              />
                            </td>
                            <td style={tbl.td}>
                              {t.hasCredential ? (
                                <span style={{ fontVariantNumeric: "tabular-nums", color: C.textMed }}>
                                  {t.credentialHint}
                                </span>
                              ) : (
                                <Pill tone="orange" label="Not set" />
                              )}
                            </td>
                            <td style={tbl.td}>
                              {t.assignedScreen ? (
                                <span style={{ fontWeight: 600 }}>
                                  {t.assignedScreen.name || t.assignedScreen.screenCode}
                                  <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400 }}>
                                    {t.assignedScreen.screenCode}
                                  </div>
                                </span>
                              ) : (
                                <span style={{ color: C.textLight }}>Unassigned</span>
                              )}
                            </td>
                            <td style={tbl.td}>
                              <Pill tone={t.active ? "green" : "grey"} label={t.active ? "Active" : "Inactive"} />
                            </td>
                            <td style={{ ...tbl.td, textAlign: "right" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                {t.spanType === "nearpay" && (
                                  <ActionIcon
                                    icon={tokenBusyId === t.id ? RefreshCw : Key}
                                    color={t.hasCredential ? C.textMed : C.green}
                                    disabled={tokenBusyId === t.id || !t.terminalId}
                                    title={
                                      !t.terminalId
                                        ? "Set the TID first — the token is signed against it"
                                        : t.hasCredential
                                          ? "Regenerate token — replaces the credential this screen is using"
                                          : "Generate token from the merchant's NearPay key"
                                    }
                                    onClick={() => requestToken(t)}
                                  />
                                )}
                                <ActionIcon
                                  icon={Edit2}
                                  color={C.blue}
                                  title="Edit terminal"
                                  onClick={() => openEdit(t)}
                                />
                                <ActionIcon
                                  icon={Power}
                                  color={t.active ? C.orange : C.green}
                                  title={
                                    t.active
                                      ? "Deactivate — the screen holding it stops taking card payment"
                                      : "Activate"
                                  }
                                  onClick={() => toggleActive(t)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </RCard>
            </>
          )}
        </div>
      </Container>

      {/* ── Create / edit ─────────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered size="lg">
        <ModalHeader
          toggle={() => setModalOpen(false)}
          style={{ borderBottom: `2px solid ${C.borderLight}` }}
        >
          <span style={{ fontWeight: 700, color: C.text }}>
            {editing ? `Edit ${editing.name}` : "Add Payment Terminal"}
          </span>
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            <div>
              <label style={modalLabelStyle}>Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Kiosk 1 terminal"
                style={modalInputStyle}
              />
            </div>

            <div>
              <label style={modalLabelStyle}>Terminal Type</label>
              <select
                value={form.spanType}
                onChange={(e) => setForm({ ...form, spanType: e.target.value })}
                style={modalInputStyle}
              >
                <option value="nearpay">NearPay (tap on device)</option>
                <option value="neoleap">NeoLeap</option>
              </select>
            </div>

            {isAdmin && (
              <div>
                <label style={modalLabelStyle}>Merchant *</label>
                <select
                  value={form.MerchantId}
                  onChange={(e) => handleMerchantChange(e.target.value)}
                  style={modalInputStyle}
                  disabled={!!editing}
                >
                  <option value="">Select merchant</option>
                  {merchants.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={modalLabelStyle}>Branch *</label>
              <select
                value={form.branchId}
                onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                style={modalInputStyle}
              >
                <option value="">Select branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>
                Only devices in this branch may use the terminal.
              </div>
            </div>

            <div>
              <label style={modalLabelStyle}>TID</label>
              <input
                value={form.terminalId}
                onChange={(e) => setForm({ ...form, terminalId: e.target.value })}
                placeholder="As printed by the acquirer"
                style={modalInputStyle}
              />
            </div>

            <div>
              <label style={modalLabelStyle}>Environment</label>
              <select
                value={form.environment}
                onChange={(e) => setForm({ ...form, environment: e.target.value })}
                style={modalInputStyle}
              >
                <option value="production">Production</option>
                <option value="sandbox">Sandbox</option>
                <option value="testing">Testing</option>
              </select>
            </div>

            {/* Read-only, and absent entirely when adding.

                This id belongs to Odoo's own span-machine record and is written by the sync. Typing
                one here would be a guess at a foreign primary key: a wrong value points our terminal
                at somebody else's Odoo record, and a right-looking one would be overwritten by the
                next sync anyway. So it is shown, never entered. */}
            {editing && (
              <div>
                <label style={modalLabelStyle}>Merchant Span Machine ID</label>
                <div
                  style={{
                    ...modalInputStyle,
                    background: C.bg,
                    color: form.merchant_span_machine_id ? C.text : C.textLight,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {form.merchant_span_machine_id || "Not synced from Odoo yet"}
                </div>
                <div style={{ fontSize: 12, color: C.textLight, marginTop: 4, lineHeight: 1.5 }}>
                  Set by the Odoo span-machine sync, not by hand.
                </div>
              </div>
            )}
          </div>

          {/* Credential gets its own full-width row: it is the long, sensitive one.

              For NearPay this is now the FALLBACK path, not the normal one — the usual route is the
              Generate Token action on the row, which signs from the merchant's uploaded key. Manual
              paste stays for terminals whose signing key we do not hold (a token issued by someone
              else, or a neoleap reader). */}
          <div style={{ marginTop: 16 }}>
            <label style={modalLabelStyle}>
              <Key size={12} style={{ marginRight: 4 }} />
              {form.spanType === "nearpay" ? "NearPay JWT (optional)" : "Credential"}
            </label>
            <textarea
              value={form.credential}
              onChange={(e) => setForm({ ...form, credential: e.target.value })}
              rows={4}
              placeholder={
                editing?.hasCredential
                  ? `Stored (${editing.credentialHint}). Paste a new one to replace it.`
                  : form.spanType === "nearpay"
                    ? "Leave blank and use Generate Token instead"
                    : "Paste the terminal's credential"
              }
              style={{ ...modalInputStyle, fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
            />
            <div style={{ fontSize: 12, color: C.textLight, marginTop: 4, lineHeight: 1.5 }}>
              {form.spanType === "nearpay"
                ? "Normally left empty — save the terminal with its TID, then press Generate Token on its row to sign one from the merchant's NearPay key."
                : editing
                  ? "Leave blank to keep the stored credential. It is never shown again once saved."
                  : "Only the device ever receives this, over its own authenticated request."}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={modalLabelStyle}>Notes</label>
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Where it is mounted, who installed it…"
              style={modalInputStyle}
            />
          </div>

          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              marginTop: 16,
              fontSize: 13,
              color: C.text,
            }}
          >
            <input
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              style={{ width: 16, height: 16, accentColor: C.coral }}
            />
            Active
          </label>
          {editing?.assignedScreen && !form.active && (
            <div style={{ fontSize: 12, color: C.orange, marginTop: 6 }}>
              Screen {editing.assignedScreen.screenCode} will stop taking card payment and go back
              to orders settled at the counter.
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: `2px solid ${C.borderLight}` }}>
          <Button color="light" onClick={() => setModalOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} style={primaryBtnStyle} disabled={!canSave || isSaving}>
            {isSaving ? "Saving…" : editing ? "Save Changes" : "Create Terminal"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Regeneration confirm. Only shown when a credential already exists — the first
          generation has nothing to invalidate, so it goes straight through. */}
      <Modal isOpen={!!tokenTarget} toggle={() => setTokenTarget(null)} centered>
        <ModalHeader toggle={() => setTokenTarget(null)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          Regenerate terminal token
        </ModalHeader>
        <ModalBody>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>
            This signs a new token for <strong>{tokenTarget?.name}</strong>
            {tokenTarget?.terminalId ? ` (${tokenTarget.terminalId})` : ""} and replaces the stored
            one.
          </div>
          {tokenTarget?.assignedScreen ? (
            <div style={{ fontSize: 13, color: C.orange, marginTop: 12, lineHeight: 1.6 }}>
              Screen {tokenTarget.assignedScreen.screenCode} is using the current token. It will not
              take card payment until it picks the new one up, which happens within 5 minutes on its
              own — nobody needs to touch the screen.
            </div>
          ) : (
            <div style={{ fontSize: 13, color: C.textMed, marginTop: 12 }}>
              No screen is holding this terminal, so nothing is interrupted.
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: `2px solid ${C.borderLight}` }}>
          <Button color="light" onClick={() => setTokenTarget(null)} disabled={!!tokenBusyId}>
            Cancel
          </Button>
          <Button
            onClick={() => generateToken(tokenTarget)}
            style={primaryBtnStyle}
            disabled={!!tokenBusyId}
          >
            {tokenBusyId ? "Signing…" : "Regenerate Token"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* The pull result.
          Shown always, not only on failure. What a pull DIDN'T import is the actionable part, and
          every reason listed here is fixed on the merchant side rather than on this page — so the
          reason is printed verbatim rather than summarised into a count. */}
      <Modal isOpen={!!syncReport} toggle={() => setSyncReport(null)} centered size="lg">
        <ModalHeader toggle={() => setSyncReport(null)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          Terminals pulled from the merchant
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 18 }}>
            {[
              ["Found", syncReport?.fetched ?? 0],
              ["Added", syncReport?.created?.length ?? 0],
              ["Updated", syncReport?.updated?.length ?? 0],
              ["Unchanged", syncReport?.unchanged ?? 0],
              ["Skipped", syncReport?.skipped?.length ?? 0],
              ["Moves refused", syncReport?.blockedMoves?.length ?? 0],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
                <div style={{ fontSize: 12, color: C.textLight }}>{label}</div>
              </div>
            ))}
          </div>

          {!!syncReport?.created?.length && (
            <div style={{ marginBottom: 16 }}>
              <div style={sectionTitleStyle}>
                <CheckCircle size={13} color={C.green} /> Added
              </div>
              {syncReport.created.map((t) => (
                <div key={`c-${t.remoteId}`} style={reportRowStyle}>
                  <strong>{t.label}</strong>
                  <span style={{ color: C.textLight }}>
                    {t.spanType} · {t.branch}
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 6, lineHeight: 1.6 }}>
                A NearPay terminal still needs its token — press Generate Token on its row — and its
                environment set, which the merchant does not hold.
              </div>
            </div>
          )}

          {!!syncReport?.updated?.length && (
            <div style={{ marginBottom: 16 }}>
              <div style={sectionTitleStyle}>
                <RefreshCw size={13} color={C.textMed} /> Updated
              </div>
              {syncReport.updated.map((t) => (
                <div key={`u-${t.remoteId}`} style={reportRowStyle}>
                  <strong>{t.label}</strong>
                  <span style={{ color: C.textLight }}>{(t.changed || []).join(", ")}</span>
                  {t.moved && (
                    <span style={{ color: C.orange }}>
                      moved {t.moved.from} → {t.moved.to}
                    </span>
                  )}
                </div>
              ))}
              {syncReport.updated.some((t) => t.moved) && (
                <div style={{ fontSize: 12, color: C.orange, marginTop: 6, lineHeight: 1.6 }}>
                  A terminal that changed branch can no longer be used by a screen in its old branch.
                  Check the screen that was holding it.
                </div>
              )}
            </div>
          )}

          {/* Refused branch moves. Deliberately ABOVE "Not imported": a screen is pointing at a
              reader the merchant says has been taken elsewhere, so this is the section with a live
              shop behind it. */}
          {!!syncReport?.blockedMoves?.length && (
            <div style={{ marginBottom: 16 }}>
              <div style={sectionTitleStyle}>
                <AlertTriangle size={13} color={C.orange} /> Branch change refused
              </div>
              {syncReport.blockedMoves.map((t) => (
                <div key={`b-${t.remoteId}`} style={reportRowStyle}>
                  <strong>{t.label}</strong>
                  <span style={{ color: C.orange, fontWeight: 600 }}>
                    {t.from} → {t.to}
                  </span>
                  <span style={{ color: C.textLight }}>held by screen {t.screenCode}</span>
                </div>
              ))}
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 6, lineHeight: 1.6 }}>
                The merchant moved these to another branch, but a screen is using them and a screen may
                only use a terminal in its own branch — so the move was not applied and the screen
                keeps working. Everything else about the terminal was updated. To accept the move,
                unassign it from that screen and pull again.
              </div>
            </div>
          )}

          {!!syncReport?.skipped?.length && (
            <div style={{ marginBottom: 16 }}>
              <div style={sectionTitleStyle}>
                <AlertTriangle size={13} color={C.orange} /> Not imported
              </div>
              {syncReport.skipped.map((t) => (
                <div key={`s-${t.remoteId}`} style={reportRowStyle}>
                  <strong>{t.label}</strong>
                  <span style={{ color: C.textMed }}>{t.reason}</span>
                  {!!t.branches?.length && (
                    <span style={{ color: C.textLight }}>{t.branches.join(", ")}</span>
                  )}
                </div>
              ))}
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 6, lineHeight: 1.6 }}>
                These are fixed on the merchant, not here — give the terminal exactly one branch, and
                free it from any POS tablet holding it. Then pull again.
              </div>
            </div>
          )}

          {!!syncReport?.notOnMerchant?.length && (
            <div>
              <div style={sectionTitleStyle}>Here but not on the merchant</div>
              {syncReport.notOnMerchant.map((t) => (
                <div key={`n-${t.remoteId}`} style={reportRowStyle}>
                  <strong>{t.label}</strong>
                </div>
              ))}
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 6, lineHeight: 1.6 }}>
                Left exactly as they are. A pull never deactivates or removes a terminal — a missing
                row could just as easily be a filtered response, and switching off a live one would
                take a shop&apos;s screen down.
              </div>
            </div>
          )}

          {!syncReport?.created?.length &&
            !syncReport?.updated?.length &&
            !syncReport?.skipped?.length && (
              <div style={{ fontSize: 13, color: C.textLight }}>
                The merchant listed no terminals for this account.
              </div>
            )}
        </ModalBody>
        <ModalFooter style={{ borderTop: `2px solid ${C.borderLight}` }}>
          <Button onClick={() => setSyncReport(null)} style={primaryBtnStyle}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default TerminalList;
